import { useMemo } from 'react';
import { createApiClient } from '../lib/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { apiEndpoints, getApiUrl } from '../config/api';

export function useApiClient() {
  const { getAccessToken, logout } = useAuth();

  const client = useMemo(
    () =>
      createApiClient({
        getToken: getAccessToken,
        onUnauthorized: logout,
      }),
    [getAccessToken, logout],
  );

  // Provide convenience methods with endpoint resolution
  return useMemo(
    () => ({
      get: <T>(endpoint: keyof typeof apiEndpoints | string) => {
        // If it's a key of apiEndpoints, look it up; otherwise treat as relative path
        const url = endpoint in apiEndpoints
          ? apiEndpoints[endpoint as keyof typeof apiEndpoints]
          : getApiUrl(endpoint);
        return client.get<T>(url);
      },
      post: <T>(
        endpoint: keyof typeof apiEndpoints | string,
        body?: unknown,
        options?: { signal?: AbortSignal },
      ) => {
        const url = endpoint in apiEndpoints
          ? apiEndpoints[endpoint as keyof typeof apiEndpoints]
          : getApiUrl(endpoint);
        return client.post<T>(url, body, options);
      },
      put: <T>(endpoint: keyof typeof apiEndpoints | string, body?: unknown) => {
        const url = endpoint in apiEndpoints
          ? apiEndpoints[endpoint as keyof typeof apiEndpoints]
          : getApiUrl(endpoint);
        return client.put<T>(url, body);
      },
      patch: <T>(endpoint: keyof typeof apiEndpoints | string, body?: unknown) => {
        const url = endpoint in apiEndpoints
          ? apiEndpoints[endpoint as keyof typeof apiEndpoints]
          : getApiUrl(endpoint);
        return client.patch<T>(url, body);
      },
      delete: <T>(endpoint: keyof typeof apiEndpoints | string) => {
        const url = endpoint in apiEndpoints
          ? apiEndpoints[endpoint as keyof typeof apiEndpoints]
          : getApiUrl(endpoint);
        return client.delete<T>(url);
      },
    }),
    [client],
  );
}
